import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, Globe, MapPin, Phone, Mail, Shield, Clock, CheckCircle2, DollarSign, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const ShippingCanadaToPakistanPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', weight: '' });
  const { toast } = useToast();
  
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ 
      title: "Quote Request Received!", 
      description: "We'll contact you within 24 hours with shipping rates to Pakistan." 
    });
    setFormData({ name: '', email: '', phone: '', weight: '' });
  };

  const pakistanCities = [
    'Lahore', 'Karachi', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Multan', 'Peshawar'
  ];

  const services = [
    'Parcels and packages to Pakistan',
    'Document courier to Pakistan',
    'Personal belongings shipping',
    'Student luggage shipping',
    'Business shipments',
    'Door-to-door delivery',
    'E-commerce shipments',
    'Gift parcels'
  ];

  return (
    <>
      <Helmet>
        <title>Shipping from Canada to Pakistan | International Courier – Shippzy</title>
        <meta name="description" content="Reliable shipping from Canada to Pakistan. Documents, parcels, luggage to Lahore, Karachi, Islamabad. Door-to-door courier. Request a custom quote." />
        <link rel="canonical" href="https://www.shippzy.ca/shipping-canada-to-pakistan" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <section className="relative bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 text-white py-24 overflow-hidden">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                <div className="inline-block mb-4 px-4 py-2 bg-green-600/30 border border-green-400/30 rounded-full">
                  <span className="text-green-300 text-sm font-semibold">🇵🇰 Canada to Pakistan Shipping</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  Shipping from Canada
                  <span className="block bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">to Pakistan</span>
                </h1>
                <p className="text-xl text-gray-200 mb-8">
                  Reliable courier service to Lahore, Karachi, Islamabad, and all Pakistan destinations. Contact us for competitive shipping rates.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-6 text-lg shadow-xl" onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}>Get Free Quote</Button>
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>View Pricing</Button>
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden md:block">
                <motion.div animate={{ y: [0, -20, 0] }} transition={{ duration: 4, repeat: Infinity }} className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-3xl p-8 shadow-2xl">
                  <Package className="w-32 h-32 text-white mx-auto mb-4" />
                  <div className="text-center"><div className="text-4xl font-bold text-white mb-2">5-10 Days</div><div className="text-green-200">Delivery to Pakistan</div></div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        <section id="pricing" className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Shipping Rates to Pakistan</h2>
              <p className="text-xl text-gray-600">Contact Shippzy for current shipping rates to Pakistan</p>
            </motion.div>
            <div className="bg-green-50 border-l-4 border-green-600 p-8 rounded-lg max-w-4xl mx-auto">
              <h3 className="font-bold text-gray-900 text-xl mb-3">Get a Custom Quote</h3>
              <p className="text-gray-700 mb-6">
                Pakistan shipping rates depend on weight, dimensions, destination city, and selected carrier. 
                Contact our team for accurate pricing tailored to your shipment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white" onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}>
                  Request Quote
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/contact'}>
                  Contact Shippzy
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <Card className="shadow-lg"><CardHeader><CardTitle className="text-2xl flex items-center gap-3"><Package className="w-8 h-8 text-green-600" />What We Ship to Pakistan</CardTitle></CardHeader><CardContent><ul className="space-y-3">{services.map((s, i) => <li key={i} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /><span className="text-gray-700">{s}</span></li>)}</ul></CardContent></Card>
              <Card className="shadow-lg border-l-4 border-green-500"><CardHeader><CardTitle className="text-2xl flex items-center gap-3"><MapPin className="w-8 h-8 text-green-600" />Pakistan Cities We Serve</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3">{pakistanCities.map((c, i) => <div key={i} className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 bg-green-600 rounded-full"></div><span>{c}</span></div>)}</div></CardContent></Card>
            </div>
          </div>
        </section>

        <section id="quote-form" className="py-20 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="shadow-2xl"><CardHeader><CardTitle className="text-3xl text-center">Get Free Quote to Pakistan</CardTitle></CardHeader><CardContent><form onSubmit={handleSubmit} className="space-y-6"><Input name="name" value={formData.name} onChange={handleChange} required placeholder="Your Name" className="h-12" /><Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Email" className="h-12" /><Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="Phone" className="h-12" /><Input name="weight" value={formData.weight} onChange={handleChange} required placeholder="Weight (e.g., 5 kg)" className="h-12" /><Button type="submit" className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white h-14 text-lg font-semibold">Get Quote Now</Button></form></CardContent></Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShippingCanadaToPakistanPage;